"use client";
import React from "react";
import { Sun, Moon } from "lucide-react";

interface ThemeTogglerButtonProps extends React.ComponentProps<"button"> {
  variant?: "default" | "accent" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  size?: "default" | "sm" | "lg";
  modes?: ("light" | "dark")[];
  direction?: "btt" | "ttb" | "ltr" | "rtl";
  onImmediateChange?: (theme: "light" | "dark") => void;
}

export const ThemeTogglerButton: React.FC<ThemeTogglerButtonProps> = ({
  variant = "default",
  size = "default",
  modes = ["light", "dark"],
  direction = "ltr",
  onImmediateChange,
  className = "",
  ...props
}) => {
  const [currentTheme, setCurrentTheme] = React.useState<"light" | "dark">("light");

  React.useEffect(() => {
    const isDark = document.documentElement.classList.contains("dark");
    setCurrentTheme(isDark ? "dark" : "light");
  }, []);

  // Listen to class changes on documentElement to stay synchronized
  React.useEffect(() => {
    const observer = new MutationObserver(() => {
      const isDark = document.documentElement.classList.contains("dark");
      setCurrentTheme(isDark ? "dark" : "light");
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  const handleClick = () => {
    const nextTheme = currentTheme === "light" ? "dark" : "light";
    if (onImmediateChange) {
      onImmediateChange(nextTheme);
    }
  };

  const buttonClasses = {
    default: "bg-white dark:bg-white/5 border border-black/5 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10 text-text-main dark:text-white shadow-sm",
    accent: "bg-primary text-white hover:bg-red-600 shadow-md shadow-primary/20",
    secondary: "bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 text-text-main dark:text-white",
    outline: "border border-border-subtle dark:border-white/20 bg-transparent text-text-main dark:text-white hover:bg-black/5 dark:hover:bg-white/5",
    ghost: "bg-transparent text-text-sub dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 hover:text-text-main hover:dark:text-white",
    destructive: "bg-red-500 text-white hover:bg-red-600 shadow-md",
    link: "bg-transparent underline-offset-4 hover:underline text-primary",
  };

  const sizeClasses = {
    default: "h-10 w-10 p-2.5 rounded-full",
    sm: "h-8 w-8 p-1.5 rounded-full",
    lg: "h-12 w-12 p-3 rounded-full",
  };

  const currentVariantClass = buttonClasses[variant] || buttonClasses.default;
  const currentSizeClass = sizeClasses[size] || sizeClasses.default;

  const rotationClass = currentTheme === "dark" ? "rotate-180" : "rotate-0";

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`relative flex items-center justify-center overflow-hidden transition-all duration-300 cursor-pointer ${currentVariantClass} ${currentSizeClass} ${className}`}
      aria-label="Toggle Theme"
      {...props}
    >
      <div className={`relative w-full h-full flex items-center justify-center transition-transform duration-500 ${rotationClass}`}>
        {currentTheme === "dark" ? (
          <Moon className="w-[85%] h-[85%] stroke-current shrink-0" />
        ) : (
          <Sun className="w-[85%] h-[85%] stroke-current shrink-0" />
        )}
      </div>
    </button>
  );
};

export default ThemeTogglerButton;
